import {enableProdMode, ErrorHandler, importProvidersFrom, inject, provideAppInitializer} from '@angular/core';
import {environment} from './environments/environment.dev';
import {authStore} from './app/core/service/auth/auth.store';
import {fetchSessionUser} from './app/core/initializers/fetchSessionUser';
import {HttpClient, provideHttpClient, withInterceptors} from '@angular/common/http';
import {getCsrfToken} from './app/core/initializers/getCsrfToken';
import {ErrorHandlerInterceptor} from './app/core/interceptor/error-handler.interceptor';
import {bootstrapApplication} from '@angular/platform-browser';
import {AppComponent} from './app/app.component';
import {provideRouter, Router} from "@angular/router";
import {appRoutes} from "./app/app.routes";
import * as Sentry from '@sentry/angular';
import {availableProductsStore} from "./app/shared/store/available-products.store";
import {
    MissingTranslationHandler,
    provideTranslateService,
    TranslateLoader,
    TranslateService
} from "@ngx-translate/core";
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {getSupportedLocales} from "./app/core/initializers/supported-locales.fetch";
import {CustomMissingTranslationHandler} from "./app/shared/translate/missing-translation.handler";
import {assemblyProfilesStore} from "./app/shared/store/assembly-profiles.store";
import {materialStore} from "./app/shared/store/material.store";
import {CustomTranslateService} from "./app/core/service/custom-translate.service";

if (environment.production) {
  enableProdMode();
}

const sentryErrorHandler = Sentry.createErrorHandler({
    showDialog: true,
    logErrors: true,
});

// Rakennetaan se provider-ehto
const errorHandlerProvider = {
    provide: ErrorHandler,
    useValue: environment.type !== 'dev'
        ? sentryErrorHandler
        : new ErrorHandler() // Angularin oletus
};

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) => new TranslateHttpLoader(http, './assets/i18n/', '.json');

Sentry.init({
    dsn: 'https://2e787a932a65ed2f45d21b126c6793b5@sentry.dicode.fi/41',
    environment: environment.production ? 'production' : 'development',
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
        // Kehitys (local API)
        'localhost/api',

        // Staging
        'napsu.kokeilussa.fi/api',

        // Väliaikainen tuotanto
        'napsu-tuotanto.kokeilussa.fi/api',

        // Tuotanto
        'napsu.purso.fi/api',
    ],
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
});

bootstrapApplication(AppComponent, {

    providers: [
        importProvidersFrom(
            //custom modules
        ),

        provideRouter(appRoutes),

        provideTranslateService({
            defaultLanguage: 'fi',


            //handles missing keys
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: CustomMissingTranslationHandler,
            },

            loader: {
                provide: TranslateLoader,
                useFactory: httpLoaderFactory,
                deps: [HttpClient]
            }

        }),

        // Lisää oma provider, joka korvaa oletuksen:
        {
            provide: TranslateService,
            useClass: CustomTranslateService,
        },

        provideHttpClient( withInterceptors([
            ErrorHandlerInterceptor
        ])),

        provideAppInitializer(()=>{
            return getSupportedLocales(inject(TranslateService), inject(HttpClient))();
        }),

        //keeps logged in user in sync with server if client is refreshed
        provideAppInitializer(() => {
            const authStoreInstance = inject(authStore);
            return fetchSessionUser(authStoreInstance)();
        }),

        //fetches csrf token on app start
        provideAppInitializer(() => {
        const initializerFn = (getCsrfToken)(inject(HttpClient));
        return initializerFn();
        }),

        //fetches available products on app start
        provideAppInitializer(() => {
            inject(availableProductsStore);
            inject(assemblyProfilesStore);
            inject(materialStore);
        }),

        errorHandlerProvider,

        {
            provide: Sentry.TraceService,
            deps: [Router],
        },

        provideAppInitializer(() => {
            inject(Sentry.TraceService); // Suorita tarvittava logiikka
            return Promise.resolve(); // Palautetaan tyhjä Promise
        }),
    ]
})
  .catch(err => console.error(err));
